import { useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ShoppingBagIcon,
  HeartIcon,
  TruckIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  CheckBadgeIcon,
  MinusIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/20/solid';

function formatPrice(value) {
  return (Number(value) || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
}

function Stars({ rating = 0, className = 'h-4 w-4' }) {
  const rounded = Math.round(Number(rating) || 0);

  return (
    <span className="flex items-center gap-0.5 text-amber-400" aria-label={`Rating ${rating} dari 5`}>
      {Array.from({ length: 5 }).map((_, index) => (
        <StarIcon
          key={index}
          className={`${className} ${index < rounded ? 'text-amber-400' : 'text-slate-200'}`}
        />
      ))}
    </span>
  );
}

function DetailSkeleton() {
  return (
    <div className="grid gap-10 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="skeleton aspect-square w-full rounded-3xl" />
        <div className="flex gap-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="skeleton h-20 w-20 rounded-2xl" />
          ))}
        </div>
      </div>
      <div className="space-y-4">
        <div className="skeleton h-4 w-24" />
        <div className="skeleton h-9 w-3/4" />
        <div className="skeleton h-4 w-40" />
        <div className="skeleton h-10 w-36" />
        <div className="skeleton h-24 w-full" />
        <div className="skeleton h-12 w-full rounded-xl" />
      </div>
    </div>
  );
}

export default function Detail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    setLoading(true);
    setError(null);
    setQuantity(1);

    axios
      .get(`/products/${id}`, { signal: controller.signal })
      .then(({ data }) => {
        if (!active) return;
        setProduct(data);
        setActiveImage(data.thumbnail || data.images?.[0] || '');
      })
      .catch((err) => {
        if (!active || axios.isCancel(err) || err.code === 'ERR_CANCELED') return;
        setError('Produk tidak ditemukan atau koneksi sedang bermasalah.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [id]);

  const handleAddToCart = () => {
    toast.success(`${quantity} × ${product.title} ditambahkan ke keranjang`);
  };

  const handleBuy = () => {
    toast('Halaman pembayaran sedang dalam perbaikan 🙏');
  };

  const handleWishlist = () => {
    toast.success('Disimpan ke wishlist');
  };

  const images = product?.images?.length ? product.images : product ? [product.thumbnail] : [];
  const originalPrice =
    product?.discountPercentage > 0
      ? product.price / (1 - product.discountPercentage / 100)
      : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container-page py-8 sm:py-12">
        <NavLink
          to="/icons"
          className="group inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition hover:text-brand-700"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200 bg-white transition group-hover:border-brand-200 group-hover:bg-brand-50">
            <ArrowLeftIcon className="h-4 w-4" strokeWidth={2.5} />
          </span>
          Kembali ke katalog
        </NavLink>

        <div className="mt-6">
          {loading ? (
            <DetailSkeleton />
          ) : error ? (
            <div className="card-surface flex flex-col items-center justify-center gap-4 px-6 py-20 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-500">
                <ShoppingBagIcon className="h-7 w-7" />
              </span>
              <div>
                <h1 className="text-lg font-bold text-slate-900">Gagal memuat produk</h1>
                <p className="mt-1 max-w-sm text-sm text-slate-600">{error}</p>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => window.location.reload()} className="btn-primary">
                  <ArrowPathIcon className="h-4 w-4" /> Coba lagi
                </button>
                <NavLink to="/icons" className="btn-secondary">
                  Ke katalog
                </NavLink>
              </div>
            </div>
          ) : product ? (
            <div className="animate-fade-up">
              {/* Breadcrumb */}
              <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-xs text-slate-400" aria-label="Breadcrumb">
                <NavLink to="/" className="transition hover:text-brand-600">Home</NavLink>
                <span>/</span>
                <NavLink to="/icons" className="transition hover:text-brand-600">Shop</NavLink>
                <span>/</span>
                <span className="max-w-[220px] truncate font-medium capitalize text-slate-600">
                  {product.title}
                </span>
              </nav>

              <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
                {/* Gallery */}
                <div className="space-y-4">
                  <div className="group relative aspect-square overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-soft">
                    <img
                      src={activeImage || product.thumbnail}
                      alt={product.title}
                      className="h-full w-full object-contain p-6 transition duration-500 group-hover:scale-105"
                    />
                    {product.discountPercentage > 0 && (
                      <span className="absolute left-4 top-4 rounded-full bg-rose-500 px-3 py-1 text-xs font-bold text-white shadow">
                        -{Math.round(product.discountPercentage)}%
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={handleWishlist}
                      aria-label="Tambah ke wishlist"
                      className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-500 shadow-sm backdrop-blur transition hover:text-rose-500"
                    >
                      <HeartIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {images.length > 1 && (
                    <div className="no-scrollbar flex gap-3 overflow-x-auto pb-1">
                      {images.map((image) => (
                        <button
                          key={image}
                          type="button"
                          onClick={() => setActiveImage(image)}
                          aria-label="Lihat gambar"
                          className={`h-20 w-20 shrink-0 overflow-hidden rounded-2xl border-2 bg-white p-1.5 transition ${
                            activeImage === image
                              ? 'border-brand-500 shadow-lift'
                              : 'border-slate-200 hover:border-brand-300'
                          }`}
                        >
                          <img src={image} alt="" className="h-full w-full object-contain" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Info */}
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    {product.brand && (
                      <span className="chip bg-brand-50 text-brand-700">{product.brand}</span>
                    )}
                    <span className="chip bg-slate-100 capitalize text-slate-600">
                      {String(product.category || '-').replaceAll('-', ' ')}
                    </span>
                    {product.stock > 0 ? (
                      <span className="chip bg-emerald-50 text-emerald-700">
                        <CheckBadgeIcon className="h-3.5 w-3.5" /> Tersedia
                      </span>
                    ) : (
                      <span className="chip bg-rose-50 text-rose-600">Stok habis</span>
                    )}
                  </div>

                  <h1 className="mt-4 text-3xl font-extrabold leading-tight text-slate-900 sm:text-4xl">
                    {product.title}
                  </h1>

                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <Stars rating={product.rating} />
                    <span className="text-sm font-semibold text-slate-700">
                      {Number(product.rating).toFixed(1)}
                    </span>
                    <span className="text-sm text-slate-400">
                      · {product.reviews?.length || 0} ulasan
                    </span>
                  </div>

                  <div className="mt-5 flex flex-wrap items-end gap-3">
                    <span className="text-4xl font-extrabold tracking-tight text-slate-900">
                      {formatPrice(product.price)}
                    </span>
                    {originalPrice && (
                      <>
                        <span className="pb-1 text-base text-slate-400 line-through">
                          {formatPrice(originalPrice)}
                        </span>
                        <span className="mb-1.5 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-600">
                          Hemat {Math.round(product.discountPercentage)}%
                        </span>
                      </>
                    )}
                  </div>

                  <p className="mt-5 text-sm leading-7 text-slate-600">
                    {product.description}
                  </p>

                  {product.tags?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {product.tags.map((tag) => (
                        <span key={tag} className="chip bg-slate-100 text-slate-600">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Quantity + actions */}
                  <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="flex h-12 items-center justify-between rounded-xl border border-slate-200 bg-white px-1.5 sm:w-36">
                      <button
                        type="button"
                        onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                        disabled={quantity <= 1}
                        aria-label="Kurangi jumlah"
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 disabled:opacity-40"
                      >
                        <MinusIcon className="h-4 w-4" strokeWidth={2.5} />
                      </button>
                      <span className="min-w-8 text-center text-sm font-bold text-slate-900">
                        {quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setQuantity((value) =>
                            product.stock ? Math.min(product.stock, value + 1) : value + 1
                          )
                        }
                        disabled={product.stock === 0}
                        aria-label="Tambah jumlah"
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 disabled:opacity-40"
                      >
                        <PlusIcon className="h-4 w-4" strokeWidth={2.5} />
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={handleAddToCart}
                      disabled={product.stock === 0}
                      className="btn-secondary flex-1"
                    >
                      <ShoppingBagIcon className="h-5 w-5" strokeWidth={1.8} />
                      Tambah ke keranjang
                    </button>

                    <button
                      type="button"
                      onClick={handleBuy}
                      disabled={product.stock === 0}
                      className="btn-primary flex-1"
                    >
                      Beli sekarang
                    </button>
                  </div>

                  {/* Meta */}
                  <div className="mt-7 grid gap-3 rounded-2xl border border-slate-200/70 bg-white p-4 text-sm sm:grid-cols-2">
                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                        <TruckIcon className="h-4 w-4" />
                      </span>
                      <div>
                        <p className="font-semibold text-slate-800">Pengiriman</p>
                        <p className="text-xs text-slate-500">
                          {product.shippingInformation || '1–3 hari kerja'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                        <ShieldCheckIcon className="h-4 w-4" />
                      </span>
                      <div>
                        <p className="font-semibold text-slate-800">Garansi</p>
                        <p className="text-xs text-slate-500">
                          {product.warrantyInformation || 'Garansi resmi'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-xs text-slate-500">
                    {product.sku && (
                      <div className="flex justify-between gap-2">
                        <dt>SKU</dt>
                        <dd className="font-medium text-slate-700">{product.sku}</dd>
                      </div>
                    )}
                    {product.weight != null && (
                      <div className="flex justify-between gap-2">
                        <dt>Berat</dt>
                        <dd className="font-medium text-slate-700">{product.weight} g</dd>
                      </div>
                    )}
                    {product.minimumOrderQuantity && (
                      <div className="flex justify-between gap-2">
                        <dt>Min. order</dt>
                        <dd className="font-medium text-slate-700">
                          {product.minimumOrderQuantity} pcs
                        </dd>
                      </div>
                    )}
                    {product.returnPolicy && (
                      <div className="flex justify-between gap-2">
                        <dt>Retur</dt>
                        <dd className="text-right font-medium text-slate-700">
                          {product.returnPolicy}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
